import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import Player from 'lottie-react';
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";
import theme from "../../util/theme/theme";
import okAnimation from '../../files/json/approved.json';
import searchingAnimation from '../../files/json/searching.json';
import { StepLineIndicator } from "../ui/step-indicator";
import { useToast } from "../../providers/toaster-provider";
import { FileDetails } from "../../pages/transactions";
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import { X } from 'lucide-react';
import { useSettings } from "../../providers/settings-provider";
import axios from 'axios';
import Cookies from 'js-cookie';
import { useUser } from '../../providers/user-provider';

interface InvoiceProvider {
  id: number;
  name: string;
  country?: string;
  importFormat?: string;
}

interface SelectFileStepProps {
  file: File | null;
  errors: { file?: string };
  loading?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (file: File | null) => void;
  formatFileSize: (size: number) => string;
  providers: InvoiceProvider[];
  selectedProviderId: number | null;
  onProviderChange: (id: number | null) => void;
  fileDetails?: FileDetails | null;
}

const SelectFileStep: React.FC<SelectFileStepProps> = ({
  file,
  errors,
  loading,
  fileInputRef,
  onFileChange,
  formatFileSize,
  providers,
  selectedProviderId,
  onProviderChange,
  fileDetails,
}) => {
  const providerOptions: DropdownOption[] = providers.map((provider) => ({
    value: String(provider.id),
    label: provider.country 
      ? `${provider.name} (${provider.country})`
      : provider.name,
  }));

  const selectedProvider = providers.find(p => p.id === selectedProviderId);
  const formatConfig = selectedProvider?.importFormat
    ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
    : null;

  // Get expected headers
  const expectedClientHeaders = (formatConfig?.clientMapping 
    ? Object.values(formatConfig.clientMapping).filter((col: any) => col !== '') as string[]
    : []) as string[];
  const expectedInvoiceHeaders = (formatConfig?.invoiceMapping
    ? (Object.values(formatConfig.invoiceMapping).filter((col: any) => col !== '' && typeof col === 'string') as string[])
    : []) as string[];
  const expectedLineItemHeaders = (formatConfig?.invoiceMapping?.lineItemMapping
    ? Object.values(formatConfig.invoiceMapping.lineItemMapping).filter((col: any) => col !== '') as string[]
    : []) as string[];

  // Compare file headers with expected headers
  const uploadedHeaders = (fileDetails?.headers || []) as string[];
  const missingHeaders = [...expectedClientHeaders, ...expectedInvoiceHeaders, ...expectedLineItemHeaders].filter(
    (header: string) => !uploadedHeaders.includes(header)
  );
  const extraHeaders = uploadedHeaders.filter(
    (header: string) => ![...expectedClientHeaders, ...expectedInvoiceHeaders, ...expectedLineItemHeaders].includes(header)
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
      <Box mb={2} sx={{ width: '100%', maxWidth: 600 }}>
        <Typography fontSize={14} color="var(--color-text-muted)" mb={1}>
          Invoice Provider <span style={{ color: '#ff3b3b' }}>*</span>
        </Typography>
        <Dropdown
          options={providerOptions}
          value={selectedProviderId ? String(selectedProviderId) : ''}
          onChange={(value) => onProviderChange(value ? Number(value) : null)}
          placeholder="Select a provider..."
          height={40}
          isPrimary={!!selectedProviderId}
        />
      </Box>
      <Typography fontSize={14} color="var(--color-text-muted)" mb={1} sx={{ width: '100%', maxWidth: 600 }}>
        Choose a File <span style={{ color: '#ff3b3b' }}>*</span>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--color-container-background)',
          borderRadius: 1,
          border: `1px solid ${errors.file ? '#e31616ff' : '#e0e0e0'}`,
          px: 2,
          py: 1.2,
          width: '100%',
          maxWidth: 600,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            color: file ? 'inherit' : '#b0b0b0',
            fontSize: 15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file
            ? `${file.name} (${formatFileSize(file.size)})`
            : 'CSV, TSV formats, up to 50MB'}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.txt"
          style={{ display: 'none' }}
          onChange={(e) => {
            const selectedFile = e.target.files?.[0] || null;
            onFileChange(selectedFile);
            if (e.target) {
              e.target.value = '';
            }
          }}
        />
        <Button
          variant="outlined"
          size="small"
          sx={{
            ml: 2,
            borderRadius: 2,
            minWidth: 80,
            textTransform: 'none',
            fontWeight: 600,
          }}
          onClick={() => fileInputRef.current?.click()}
          disabled={loading || !selectedProviderId}
        >
          Upload
        </Button>
      </Box>
      {errors.file && (
        <Box mt={0.5}>
          <Typography fontSize={12} color="#dc1111ff" pl={2}>
            {errors.file}
          </Typography>
        </Box>
      )}
      {formatConfig && (!file || !fileDetails?.headers || (missingHeaders.length === 0 && extraHeaders.length === 0)) && (
        <Box my={1} sx={{ width: '100%', maxWidth: 600 }}>
          <Box 
            sx={{
              backgroundColor: 'var(--color-container-background)',
              border: '1px solid #e0e0e0',
              borderRadius: 1.5,
              p: 1
            }}
          >
            {formatConfig.clientMapping && (
              <Box mb={0.5}>
                <Typography fontSize={12} fontWeight={600} color="var(--color-text-muted)" mb={0.75}>
                  Expected Client Fields:
                </Typography>
                <Typography fontSize={12} color="var(--color-text-muted)">
                  {Object.values(formatConfig.clientMapping).filter((col: any) => col !== '').join(', ')}
                </Typography>
              </Box>
            )}
            {formatConfig.invoiceMapping && (
              <Box>
                <Typography fontSize={12} fontWeight={600} color="var(--color-text-muted)" mb={0.75}>
                  Expected Invoice Fields:
                </Typography>
                <Typography fontSize={12} color="var(--color-text-muted)">
                  {Object.values(formatConfig.invoiceMapping).filter((col: any) => col !== '' && typeof col === 'string').join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {file && fileDetails?.headers && missingHeaders.length > 0 && (
        <Box mt={1} sx={{ width: '100%', maxWidth: 600 }}>
          <Box
            sx={{
              backgroundColor: '#fff8f0',
              border: '1px solid #ffe0cc',
              borderRadius: 1.5,
              p: 2
            }}
          >
            {missingHeaders.length > 0 && (
              <>
              <Box mb={0.5}>
                <Typography fontSize={12} fontWeight={600} color="#ff6b35" mb={0.5}>
                  Missing Expected Columns:
                </Typography>
                <Typography fontSize={12} color="#d84315">
                  {missingHeaders.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography fontSize={12} fontWeight={600} color="#ff9800" mb={0.5}>
                  Columns Found:
                </Typography>
                <Typography fontSize={12} color="#e65100">
                  {extraHeaders.join(', ')}
                </Typography>
              </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface ClientDuplicateCheckStepProps {
  isProcessing: boolean;
  duplicates: any[];
  fileDetails?: FileDetails | null;
  duplicateCheckProgress?: { currentChunk: number; totalChunks: number; processedRows: number; totalRows: number };
  selectedProvider?: InvoiceProvider | null;
}

const ClientDuplicateCheckStep: React.FC<ClientDuplicateCheckStepProps> = ({
  isProcessing,
  duplicates,
  fileDetails,
  duplicateCheckProgress = { currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 },
  selectedProvider,
}) => {
  const formatConfig = selectedProvider?.importFormat 
    ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
    : null;
  
  const fieldLabels: Record<string, string> = {
    company: 'Company',
    recepient: 'Recipient',
    phone: 'Phone',
    email: 'Email',
    fax: 'Fax',
    address1: 'Street Address Line 1',
    address2: 'Street Address Line 2',
    suburb: 'Suburb',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country'
  };

  const clientMappingKeys = formatConfig?.clientMapping 
    ? Object.entries(formatConfig.clientMapping)
        .filter(([_, csvCol]: [string, any]) => csvCol !== '')
        .map(([fieldName, csvCol]) => ({ fieldName, csvCol: csvCol as string }))
    : [];

  const columns: MRT_ColumnDef<Record<string, any>>[] = clientMappingKeys.map(({ fieldName, csvCol }) => ({
    accessorKey: csvCol,
    header: fieldLabels[fieldName] || fieldName,
    size: 150,
  }));

  return (
    <Box>
      {isProcessing ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-container-background)'
          }}
        >
          <Player
            autoplay
            loop
            animationData={searchingAnimation}
            style={{ height: 160, width: 160 }}
          />
          <Box sx={{ width: '80%', maxWidth: 400, mb: 2 }}>
            <Box 
              sx={{ 
                height: 8, 
                borderRadius: 4, 
                background: '#e0e0e0',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  height: '100%', 
                  background: 'var(--color-primary)',
                  width: `${duplicateCheckProgress.totalRows > 0 ? (duplicateCheckProgress.processedRows / duplicateCheckProgress.totalRows) * 100 : 0}%`,
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
              <Typography variant="body2" color="text.secondary">
                Checking for duplicate clients...
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {duplicateCheckProgress.processedRows} / {duplicateCheckProgress.totalRows} clients analyzed
            </Typography>
          </Box>
        </Box>
      ) : duplicates.length === 0 ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-container-background)'
          }}
        >
          <Player
            autoplay
            loop
            animationData={okAnimation}
            style={{ height: 160, width: 160 }}
          />
          <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: 'var(--color-primary)' }}>
            No duplicate clients found
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box 
            sx={{ 
              color: 'var(--color-primary)',
              padding: 0,
              textAlign: 'center',
              minHeight: 75,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--color-container-background)'
            }}
          >
            {!duplicates.length &&
              <Player
                autoplay
                loop
                animationData={okAnimation}
                style={{ height: 160, width: 160 }}
              />
            }
            <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: '#ff9800' }}>
              Duplicate client(s) found
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              {duplicates.length} {duplicates.length === 1 ? 'client was' : 'clients were'} found as duplicates.
            </Typography>
          </Box>
          <Box position="relative" sx={{ mt: 1 }}>
            <MaterialReactTable
              columns={columns}
              data={duplicates as Record<string, any>[]}
              enableRowVirtualization
              enableColumnVirtualization
              rowVirtualizerOptions={{ overscan: 20 }}
              columnVirtualizerOptions={{ overscan: 2 }}
              enablePagination={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enableColumnActions={false}
              initialState={{ density: 'compact' }}
              muiTablePaperProps={{
                sx: { boxShadow: 'none' },
              }}
              muiTableContainerProps={{
                sx: { maxHeight: 260, background: 'var(--color-container-background)' },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface ReviewClientStepProps {
  fileDetails?: FileDetails | null;
  dedupedClients?: any[];
  selectedRowIds: Set<string>;
  selectedProvider?: InvoiceProvider | null;
  duplicates?: any[];
}

const ReviewClientStep: React.FC<ReviewClientStepProps> = ({ fileDetails, dedupedClients, selectedRowIds, selectedProvider, duplicates = [] }) => {
  const formatConfig = selectedProvider?.importFormat 
    ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
    : null;

  const fieldLabels: Record<string, string> = {
    company: 'Company',
    recepient: 'Recipient',
    phone: 'Phone',
    email: 'Email',
    fax: 'Fax',
    address1: 'Street Address Line 1',
    address2: 'Street Address Line 2',
    suburb: 'Suburb',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country'
  };

  const clientMappingKeys = formatConfig?.clientMapping 
    ? Object.entries(formatConfig.clientMapping)
        .filter(([_, csvCol]: [string, any]) => csvCol !== '')
        .map(([fieldName, csvCol]) => ({ fieldName, csvCol: csvCol as string }))
    : [];

  const columns: MRT_ColumnDef<Record<string, any>>[] = clientMappingKeys.map(({ fieldName, csvCol }) => ({
    accessorKey: csvCol,
    header: fieldLabels[fieldName] || fieldName,
    size: 150,
  }));

  const selectedRows = (dedupedClients || [])
    .filter((row: any) => selectedRowIds.has((row as any).__rowId))
    .filter((row: any) => {
      // Exclude duplicate clients found by API
      return !duplicates.some(dup => (dup as any).__rowId === (row as any).__rowId);
    });
  
  if (selectedRows.length === 0) {
    return (
      <Box 
        sx={{ 
          color: 'var(--color-primary)',
          padding: 4,
          textAlign: 'center',
          minHeight: 75,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-container-background)'
        }}
      >
        <Player
          autoplay
          loop
          animationData={searchingAnimation}
          style={{ height: 160, width: 160 }}
        />
        <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: '#ff9800' }}>
          No clients selected
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography fontSize={14} color="var(--color-text-muted)" mb={2}>
        Ready to import {selectedRows.length} client{selectedRows.length !== 1 ? 's' : ''}
      </Typography>
      <MaterialReactTable
        columns={columns}
        data={selectedRows as Record<string, any>[]}
        enableRowVirtualization
        enableColumnVirtualization
        rowVirtualizerOptions={{ overscan: 20 }}
        columnVirtualizerOptions={{ overscan: 2 }}
        enablePagination={false}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enableColumnActions={false}
        initialState={{ density: 'compact' }}
        muiTableContainerProps={{ sx: { maxHeight: 300, overflowY: 'auto', position: 'relative' } }}
        muiTablePaperProps={{
          sx: { boxShadow: 'none', background: 'var(--color-container-background)' },
        }}
      />
    </Box>
  );
};

interface ClientImportProgressStepProps {
  isProcessing: boolean;
  status: 'processing' | 'success' | 'error';
  importedEntries: any[];
}

const ClientImportProgressStep: React.FC<ClientImportProgressStepProps> = ({ 
  isProcessing, 
  status,
  importedEntries,
}) => {
  if (isProcessing) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={3}>
        <CircularProgress size={48} sx={{ color: 'var(--color-primary)' }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Importing clients...
        </Typography>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box position="relative">
        {importedEntries.length === 0 ? (
          <Box 
            sx={{ 
              border: '1px solid #FFD700',
              borderRadius: 2,
              backgroundColor: 'rgba(255, 217, 0, 0.13)',
              color: '#B8860B',
              padding: 4,
              textAlign: 'center',
              minHeight: 75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
              No clients were imported
            </Typography>
          </Box>
        ) : (
          <>
            <Typography fontSize={14} color="var(--color-text-muted)" mb={2}>
              Successfully imported {importedEntries.length} client{importedEntries.length !== 1 ? 's' : ''}
            </Typography>
            <MaterialReactTable
              columns={[
                {
                  accessorKey: 'companyName',
                  header: 'Company Name',
                  size: 180,
                },
                {
                  accessorKey: 'abbr',
                  header: 'Abbreviation',
                  size: 100,
                },
                {
                  accessorKey: 'recipient',
                  header: 'Receipient',
                  size: 150,
                },
              ] as MRT_ColumnDef<Record<string, any>>[]}
              data={importedEntries as Record<string, any>[]}
              enableRowVirtualization
              enableColumnVirtualization
              rowVirtualizerOptions={{ overscan: 20 }}
              columnVirtualizerOptions={{ overscan: 2 }}
              enablePagination={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enableColumnActions={false}
              initialState={{ density: 'compact' }}
              muiTableContainerProps={{ sx: { maxHeight: 300, overflowY: 'auto', position: 'relative' } }}
              muiTablePaperProps={{
                sx: { boxShadow: 'none', background: 'var(--color-container-background)' },
              }}
            />
          </>
        )}
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        border: '1px solid #e31616ff',
        borderRadius: 2,
        backgroundColor: 'rgba(227, 22, 22, 0.1)',
        color: '#e31616ff',
        padding: 4,
        textAlign: 'center',
        minHeight: 75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
        Client import failed. Please try again.
      </Typography>
    </Box>
  );
};

interface InvoiceDuplicateCheckStepProps {
  isProcessing: boolean;
  duplicates: any[];
  fileDetails?: FileDetails | null;
  duplicateCheckProgress?: { currentChunk: number; totalChunks: number; processedRows: number; totalRows: number };
  selectedProvider?: InvoiceProvider | null;
}

const InvoiceDuplicateCheckStep: React.FC<InvoiceDuplicateCheckStepProps> = ({
  isProcessing,
  duplicates,
  fileDetails,
  duplicateCheckProgress = { currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 },
  selectedProvider,
}) => {
  const formatConfig = selectedProvider?.importFormat 
    ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
    : null;

  const fieldLabels: Record<string, string> = {
    extInvoiceNumber: 'Invoice Number',
    date: 'Invoice Date',
    dueDate: 'Due Date',
    amount: 'Amount',
    taxAmount: 'Tax Amount',
    amountPaid: 'Amount Paid',
    amountDue: 'Amount Due',
    currency: 'Currency',
    status: 'Status',
    descriptor: 'Notes',
    company: 'Company',
    recepient: 'Recipient',
    phone: 'Phone',
    email: 'Email',
    fax: 'Fax',
    address1: 'Street Address Line 1',
    address2: 'Street Address Line 2',
    suburb: 'Suburb',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country'
  };

  const lineItemLabels: Record<string, string> = {
    description: 'Description',
    quantity: 'Quantity',
    unitPrice: 'Unit Price',
    taxName: 'Tax Type',
    taxAmount: 'Tax Amount',
    lineTotal: 'Line Total'
  };

  const addressFields = ['address1', 'address2', 'suburb', 'state', 'postalCode', 'country', 'recepient', 'phone', 'fax', 'email'];

  const renderAddress = (row: any, mapping: any) => {
    const recipientName = mapping.recepient ? row[mapping.recepient] : '';
    const address1 = mapping.address1 ? row[mapping.address1] : '';
    const address2 = mapping.address2 ? row[mapping.address2] : '';
    const suburb = mapping.suburb ? row[mapping.suburb] : '';
    const state = mapping.state ? row[mapping.state] : '';
    const postalCode = mapping.postalCode ? row[mapping.postalCode] : '';
    const phone = mapping.phone ? row[mapping.phone] : '';
    const fax = mapping.fax ? row[mapping.fax] : '';
    const email = mapping.email ? row[mapping.email] : '';

    return (
      <Box>
        {recipientName && (
          <Typography sx={{ fontSize: 11, color: 'var(--color-default-text)', fontWeight: 500 }}>
            {recipientName}
          </Typography>
        )}
        {(address1 || address2 || suburb || state || postalCode) && (
          <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
            {[address1, address2, suburb, state, postalCode].filter(Boolean).join(', ')}
          </Typography>
        )}
        {(phone || fax || email) && (
          <Box display="flex" gap={2} flexWrap="wrap">
            {phone && (
              <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                <span style={{ fontWeight: 500 }}>P:</span> {phone}
              </Typography>
            )}
            {fax && (
              <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                <span style={{ fontWeight: 500 }}>F:</span> {fax}
              </Typography>
            )}
            {email && (
              <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                <span style={{ fontWeight: 500 }}>E:</span> {email}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const invoiceMappingKeys = formatConfig?.invoiceMapping 
    ? Object.entries(formatConfig.invoiceMapping)
        .filter(([key, csvCol]: [string, any]) => key !== 'lineItemMapping' && csvCol !== '' && !addressFields.includes(key))
        .map(([fieldName, csvCol]) => ({ fieldName, csvCol: csvCol as string }))
    : [];

  const lineItemMappingKeys = formatConfig?.invoiceMapping?.lineItemMapping 
    ? Object.entries(formatConfig.invoiceMapping.lineItemMapping)
        .filter(([fieldName, csvCol]: [string, any]) => csvCol !== '' && !['taxTotal', 'lineTotal'].includes(fieldName))
        .map(([fieldName, csvCol]) => ({ fieldName, csvCol: csvCol as string }))
        .sort((a, b) => {
          const labelKeys = Object.keys(lineItemLabels);
          const aIndex = labelKeys.indexOf(a.fieldName);
          const bIndex = labelKeys.indexOf(b.fieldName);
          return aIndex - bIndex;
        })
    : [];

  // Group duplicates by invoice number
  const invoiceNumberColumn = formatConfig?.invoiceMapping?.extInvoiceNumber;
  const groupedDuplicates = React.useMemo(() => {
    const groups: Record<string, any[]> = {};
    duplicates.forEach((row: any) => {
      const invoiceNumber = row[invoiceNumberColumn] || 'Unknown';
      if (!groups[invoiceNumber]) {
        groups[invoiceNumber] = [];
      }
      groups[invoiceNumber].push(row);
    });
    return Object.entries(groups).map(([invoiceNumber, rows]) => ({
      invoiceNumber,
      invoiceRow: rows[0],
      lineItems: rows
    }));
  }, [duplicates, invoiceNumberColumn]);

  return (
    <Box>
      {isProcessing ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-container-background)'
          }}
        >
          <Player
            autoplay
            loop
            animationData={searchingAnimation}
            style={{ height: 160, width: 160 }}
          />
          <Box sx={{ width: '80%', maxWidth: 400, mb: 2 }}>
            <Box 
              sx={{ 
                height: 8, 
                borderRadius: 4, 
                background: '#e0e0e0',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  height: '100%', 
                  background: 'var(--color-primary)',
                  width: `${duplicateCheckProgress.totalRows > 0 ? (duplicateCheckProgress.processedRows / duplicateCheckProgress.totalRows) * 100 : 0}%`,
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
              <Typography variant="body2" color="text.secondary">
                Checking for duplicate invoices...
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {duplicateCheckProgress.processedRows} / {duplicateCheckProgress.totalRows} invoices analyzed
            </Typography>
          </Box>
        </Box>
      ) : groupedDuplicates.length === 0 ? (
        <Box 
          sx={{ 
            color: 'var(--color-primary)',
            padding: 4,
            textAlign: 'center',
            minHeight: 75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-container-background)'
          }}
        >
          <Player
            autoplay
            loop
            animationData={okAnimation}
            style={{ height: 160, width: 160 }}
          />
          <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: 'var(--color-primary)' }}>
            No duplicate invoices found
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box 
            sx={{ 
              color: 'var(--color-primary)',
              padding: 2,
              textAlign: 'center',
              minHeight: 75,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--color-container-background)',
              borderRadius: 1,
              mb: 2
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: '#ff9800' }}>
              Duplicate invoice(s) found
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
              {groupedDuplicates.length} {groupedDuplicates.length === 1 ? 'invoice was' : 'invoices were'} found as duplicates.
            </Typography>
          </Box>
          <Box sx={{ maxHeight: 250, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {groupedDuplicates.map((invoice, idx) => (
              <Box
                key={idx}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  background: idx % 2 === 0 ? 'rgba(var(--color-primary-rgb), 0.0824)' : '#ffffff',
                  p: 1
                }}
              >
                {/* Invoice Header */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', borderBottom: '1px solid #e0e0e0', pb: 0.5 }}>
                  {invoiceMappingKeys.map(({ fieldName, csvCol }) => (
                    <Box key={fieldName} sx={{ height: 'fit-content', display: 'flex', flexDirection: 'column'}}>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: 10, fontWeight: 500 }}>
                        {fieldLabels[fieldName] || fieldName}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 500 }}>
                        {invoice.invoiceRow[csvCol] || '-'}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Address */}
                <Box sx={{ my: 0.5 }}>
                  {renderAddress(invoice.invoiceRow, formatConfig?.invoiceMapping || {})}
                </Box>

                {/* Line Items Table */}
                {lineItemMappingKeys.length > 0 && (
                  <Box>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: 'var(--color-container-background)', borderBottom: '1px solid #e0e0e0' }}>
                            {lineItemMappingKeys.map(({ fieldName }) => (
                              <th key={fieldName} style={{ padding: '4px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                                {lineItemLabels[fieldName] || fieldName}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.lineItems.map((item, itemIdx) => (
                            <tr key={itemIdx} style={{ borderBottom: '1px solid #f0f0f0', background: itemIdx % 2 !== 0 ? 'var(--color-container-background)' : '#ffffff' }}>
                              {lineItemMappingKeys.map(({ fieldName, csvCol }) => (
                                <td key={fieldName} style={{ padding: '4px', textAlign: 'left', color: 'var(--color-default-text)' }}>
                                  {item[csvCol] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface ReviewInvoiceStepProps {
  fileDetails?: FileDetails | null;
  selectedRowIds: Set<string>;
  selectedProvider?: InvoiceProvider | null;
  duplicates?: any[];
}

const ReviewInvoiceStep: React.FC<ReviewInvoiceStepProps> = ({ fileDetails, selectedRowIds, selectedProvider, duplicates = [] }) => {
  const formatConfig = selectedProvider?.importFormat 
    ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
    : null;

  const fieldLabels: Record<string, string> = {
    extInvoiceNumber: 'Invoice Number',
    date: 'Invoice Date',
    dueDate: 'Due Date',
    amount: 'Amount',
    taxAmount: 'Tax Amount',
    amountPaid: 'Amount Paid',
    amountDue: 'Amount Due',
    currency: 'Currency',
    status: 'Status',
    descriptor: 'Notes',
    company: 'Company',
    recepient: 'Recipient',
    phone: 'Phone',
    email: 'Email',
    fax: 'Fax',
    address1: 'Street Address Line 1',
    address2: 'Street Address Line 2',
    suburb: 'Suburb',
    state: 'State',
    postalCode: 'Postal Code',
    country: 'Country'
  };

  const lineItemLabels: Record<string, string> = {
    description: 'Description',
    quantity: 'Quantity',
    unitPrice: 'Unit Price',
    taxName: 'Tax',
    taxAmount: 'Tax Amount',
    lineTotal: 'Line Total'
  };

  const addressFields = ['address1', 'address2', 'suburb', 'state', 'postalCode', 'country', 'recepient', 'phone', 'fax', 'email'];

  const renderAddress = (row: any, mapping: any) => {
    const recipientName = mapping.recepient ? row[mapping.recepient] : '';
    const address1 = mapping.address1 ? row[mapping.address1] : '';
    const address2 = mapping.address2 ? row[mapping.address2] : '';
    const suburb = mapping.suburb ? row[mapping.suburb] : '';
    const state = mapping.state ? row[mapping.state] : '';
    const postalCode = mapping.postalCode ? row[mapping.postalCode] : '';
    const phone = mapping.phone ? row[mapping.phone] : '';
    const fax = mapping.fax ? row[mapping.fax] : '';
    const email = mapping.email ? row[mapping.email] : '';

    return (
      <Box>
        {recipientName && (
          <Typography sx={{ fontSize: 11, color: 'var(--color-default-text)', fontWeight: 500 }}>
            {recipientName}
          </Typography>
        )}
        {(address1 || address2 || suburb || state || postalCode) && (
          <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
            {[address1, address2, suburb, state, postalCode].filter(Boolean).join(', ')}
          </Typography>
        )}
        {(phone || fax || email) && (
          <Box display="flex" gap={2} flexWrap="wrap">
            {phone && (
              <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                <span style={{ fontWeight: 500 }}>P:</span> {phone}
              </Typography>
            )}
            {fax && (
              <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                <span style={{ fontWeight: 500 }}>F:</span> {fax}
              </Typography>
            )}
            {email && (
              <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
                <span style={{ fontWeight: 500 }}>E:</span> {email}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const invoiceMappingKeys = formatConfig?.invoiceMapping 
    ? Object.entries(formatConfig.invoiceMapping)
        .filter(([key, csvCol]: [string, any]) => key !== 'lineItemMapping' && csvCol !== '' && !addressFields.includes(key))
        .map(([fieldName, csvCol]) => ({ fieldName, csvCol: csvCol as string }))
    : [];

  const lineItemMappingKeys = formatConfig?.invoiceMapping?.lineItemMapping 
    ? Object.entries(formatConfig.invoiceMapping.lineItemMapping)
        .filter(([fieldName, csvCol]: [string, any]) => csvCol !== '' && !['taxTotal', 'lineTotal'].includes(fieldName))
        .map(([fieldName, csvCol]) => ({ fieldName, csvCol: csvCol as string }))
        .sort((a, b) => {
          const labelKeys = Object.keys(lineItemLabels);
          const aIndex = labelKeys.indexOf(a.fieldName);
          const bIndex = labelKeys.indexOf(b.fieldName);
          return aIndex - bIndex;
        })
    : [];

  const selectedRows = (fileDetails?.parsedRows || [])
    .filter((row: any) => selectedRowIds.has((row as any).__rowId))
    .filter((row: any) => {
      // Exclude duplicate invoices found by API
      return !duplicates.some(dup => (dup as any).__rowId === (row as any).__rowId);
    })
    .filter((row: any) => {
      // Exclude invoices based on amount threshold if rule is enabled
      const excludeAmountRule = formatConfig?.excludeAmountRule ?? false;
      const excludeAmountThreshold = formatConfig?.excludeAmountThreshold ?? 0;
      const amountColumn = formatConfig?.invoiceMapping?.amount;
      
      if (!excludeAmountRule || !amountColumn) {
        return true; // Keep the invoice if rule is not enabled or no amount column
      }
      
      const amount = parseFloat(String(row[amountColumn] || 0));
      // return amount > excludeAmountThreshold; // Keep invoices with amount > threshold
      return Math.abs(amount) > excludeAmountThreshold;
    });

  // Group rows by invoice number
  const invoiceNumberColumn = formatConfig?.invoiceMapping?.extInvoiceNumber;
  const groupedInvoices = React.useMemo(() => {
    const groups: Record<string, any[]> = {};
    selectedRows.forEach((row: any) => {
      const invoiceNumber = row[invoiceNumberColumn] || 'Unknown';
      if (!groups[invoiceNumber]) {
        groups[invoiceNumber] = [];
      }
      groups[invoiceNumber].push(row);
    });
    return Object.entries(groups).map(([invoiceNumber, rows]) => ({
      invoiceNumber,
      invoiceRow: rows[0],
      lineItems: rows
    }));
  }, [selectedRows, invoiceNumberColumn]);
  
  if (groupedInvoices.length === 0) {
    return (
      <Box 
        sx={{ 
          color: 'var(--color-primary)',
          padding: 4,
          textAlign: 'center',
          minHeight: 75,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-container-background)'
        }}
      >
        <Player
          autoplay
          loop
          animationData={searchingAnimation}
          style={{ height: 160, width: 160 }}
        />
        <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic', color: '#ff9800' }}>
          No invoices selected
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography fontSize={14} color="var(--color-text-muted)" mb={1}>
        Ready to import {groupedInvoices.length} invoice{groupedInvoices.length !== 1 ? 's' : ''}
      </Typography>
      <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {groupedInvoices.map((invoice, idx) => (
          <Box
            key={idx}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              background: idx % 2 === 0 ? 'rgba(var(--color-primary-rgb), 0.0824)' : '#ffffff',
              p: 1
            }}
          >
            {/* Invoice Header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', borderBottom: '1px solid #e0e0e0', pb: 0.5 }}>
              {invoiceMappingKeys.map(({ fieldName, csvCol }) => (
                <Box key={fieldName} sx={{ height: 'fit-content', display: 'flex', flexDirection: 'column'}}>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: 10, fontWeight: 500 }}>
                    {fieldLabels[fieldName] || fieldName}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 500 }}>
                    {invoice.invoiceRow[csvCol] || '-'}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Address */}
            <Box sx={{ my: 0.5 }}>
              {renderAddress(invoice.invoiceRow, formatConfig?.invoiceMapping || {})}
            </Box>

            {/* Line Items Table */}
            {lineItemMappingKeys.length > 0 && (
              <Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--color-container-background)', borderBottom: '1px solid #e0e0e0' }}>
                        {lineItemMappingKeys.map(({ fieldName }) => (
                          <th key={fieldName} style={{ padding: '4px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                            {lineItemLabels[fieldName] || fieldName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.lineItems.map((item, itemIdx) => (
                        <tr key={itemIdx} style={{ borderBottom: '1px solid #f0f0f0', background: itemIdx % 2 !== 0 ? 'var(--color-container-background)' : '#ffffff' }}>
                          {lineItemMappingKeys.map(({ fieldName, csvCol }) => (
                            <td key={fieldName} style={{ padding: '4px', textAlign: 'left', color: 'var(--color-default-text)' }}>
                              {item[csvCol] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface NothingToImportStepProps {
  clientsImported?: number;
}

const NothingToImportStep: React.FC<NothingToImportStepProps> = ({ clientsImported = 0 }) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={3}>
      <Box
        sx={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: 2,
          padding: 3,
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
          Nothing to Import
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: 2 }}>
          {clientsImported > 0
            ? `All invoices have been filtered out or marked as duplicates. ${clientsImported} client(s) were imported successfully.`
            : 'All invoices have been filtered out or marked as duplicates.'}
        </Typography>
      </Box>
    </Box>
  );
};

interface InvoiceImportProgressStepProps {
  isProcessing: boolean;
  status: 'processing' | 'success' | 'error';
  importedEntries: any[];
  clientsFound?: number;
  clientsImported?: number;
  invoicesFound?: number;
  invoicesImported?: number;
  importProgress?: { currentChunk: number; totalChunks: number; processedRows: number; totalRows: number };
}

const InvoiceImportProgressStep: React.FC<InvoiceImportProgressStepProps> = ({ 
  isProcessing, 
  status,
  importedEntries,
  clientsFound = 0,
  clientsImported = 0,
  invoicesFound = 0,
  invoicesImported = 0,
  importProgress = { currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 },
}) => {
  if (isProcessing) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={3}>
        <Box sx={{ width: '80%', maxWidth: 400, mb: 2 }}>
          <Box 
            sx={{ 
              height: 8, 
              borderRadius: 4, 
              background: '#e0e0e0',
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                height: '100%', 
                background: 'var(--color-primary)',
                width: `${importProgress.totalRows > 0 ? (importProgress.processedRows / importProgress.totalRows) * 100 : 0}%`,
                transition: 'width 0.3s ease'
              }}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
            <Typography variant="body2" color="text.secondary">
              Importing invoices...
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {importProgress.processedRows} / {importProgress.totalRows} invoices imported
          </Typography>
        </Box>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box position="relative" display="flex" flexDirection="column" gap={3}>
        {/* Clients Summary */}
        <Box 
          sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            padding: 3,
            backgroundColor: 'var(--color-container-background)',
          }}
        >
          <Typography fontSize={14} fontWeight={600} color="var(--color-text-muted)" mb={2}>
            Clients
          </Typography>
          <Box display="flex" justifyContent="space-between" gap={3}>
            <Box>
              <Typography fontSize={12} color="var(--color-text-muted)" mb={0.5}>
                Found
              </Typography>
              <Typography fontSize={24} fontWeight={700} color="var(--color-primary)">
                {clientsFound}
              </Typography>
            </Box>
            <Box>
              <Typography fontSize={12} color="var(--color-text-muted)" mb={0.5}>
                Imported
              </Typography>
              <Typography fontSize={24} fontWeight={700} color="var(--color-primary)">
                {clientsImported}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Invoices Summary */}
        <Box 
          sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            padding: 3,
            backgroundColor: 'var(--color-container-background)',
          }}
        >
          <Typography fontSize={14} fontWeight={600} color="var(--color-text-muted)" mb={2}>
            Invoices
          </Typography>
          <Box display="flex" justifyContent="space-between" gap={3}>
            <Box>
              <Typography fontSize={12} color="var(--color-text-muted)" mb={0.5}>
                Found
              </Typography>
              <Typography fontSize={24} fontWeight={700} color="var(--color-primary)">
                {invoicesFound}
              </Typography>
            </Box>
            <Box>
              <Typography fontSize={12} color="var(--color-text-muted)" mb={0.5}>
                Imported
              </Typography>
              <Typography fontSize={24} fontWeight={700} color="var(--color-primary)">
                {invoicesImported}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        border: '1px solid #e31616ff',
        borderRadius: 2,
        backgroundColor: 'rgba(227, 22, 22, 0.1)',
        color: '#e31616ff',
        padding: 4,
        textAlign: 'center',
        minHeight: 75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
        Invoice import failed. Please try again.
      </Typography>
    </Box>
  );
};

export interface ImportInvoicePopupProps {
  open: boolean;
  fileDetails?: FileDetails | null;
  onFileChange: (details: FileDetails) => void;
  onProviderChange: (value: number | null) => void;
  onImport: (selectedRows: any[], selectedRowIds: Set<string>, onProgress?: (state: any) => void) => void;
  onImportInvoices?: (selectedRows: any[], selectedRowIds: Set<string>, onProgress?: (state: any) => void) => void;
  onCancel: () => void;
  onEnterEditDataStep?: () => void;
  importId?: string | null;
  loading?: boolean;
}

const ImportInvoicePopup: React.FC<ImportInvoicePopupProps> = ({
  open,
  fileDetails,
  onFileChange,
  onProviderChange,
  onImport,
  onImportInvoices,
  onCancel,
  onEnterEditDataStep,
  importId,
  loading = false,
}) => {
  const { showError } = useToast();
  const { companySettings } = useSettings();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = React.useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [errors, setErrors] = React.useState<{ file?: string }>({});
  const [providers, setProviders] = React.useState<InvoiceProvider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = React.useState<number | null>(null);
  const [fetchProviders, setFetchProviders] = useApi();
  const [selectedRowIds, setSelectedRowIds] = React.useState<Set<string>>(new Set());
  const initializedFileRef = React.useRef<any>(null);
  const [clientDuplicates, setClientDuplicates] = React.useState<any[]>([]);
  const [invoiceDuplicates, setInvoiceDuplicates] = React.useState<any[]>([]);
  const [duplicateCheckProgress, setDuplicateCheckProgress] = React.useState<{ currentChunk: number; totalChunks: number; processedRows: number; totalRows: number }>({ currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 });
  const [importProgress, setImportProgress] = React.useState<{ currentChunk: number; totalChunks: number; processedRows: number; totalRows: number }>({ currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 });
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [clientImportStatus, setClientImportStatus] = React.useState<'processing' | 'success' | 'error'>('processing');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invoiceImportStatus, setInvoiceImportStatus] = React.useState<'processing' | 'success' | 'error'>('processing');
  const [importedClients, setImportedClients] = React.useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [importedInvoices, setImportedInvoices] = React.useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasProcessedStep, setHasProcessedStep] = React.useState<boolean[]>([false, false, false, false, false, false, false]);
  const [importSummary, setImportSummary] = React.useState({ clientsFound: 0, clientsImported: 0, invoicesFound: 0, invoicesImported: 0 });
  const { user } = useUser();

  // Cache for duplicate check results to avoid re-running API on step navigation
  const clientDuplicateCheckCacheRef = React.useRef<{
    fileDataHash: string;
    duplicates: any[];
  } | null>(null);

  const invoiceDuplicateCheckCacheRef = React.useRef<{
    fileDataHash: string;
    duplicates: any[];
  } | null>(null);

  // Compute dedupedClients by removing duplicates based on client mapping
  const dedupedClients = React.useMemo(() => {
    if (!fileDetails?.parsedRows || !selectedProviderId || providers.length === 0) {
      return fileDetails?.parsedRows || [];
    }

    const selectedProvider = providers.find(p => p.id === selectedProviderId);
    if (!selectedProvider) {
      return fileDetails.parsedRows;
    }

    const formatConfig = selectedProvider?.importFormat 
      ? (typeof selectedProvider.importFormat === 'string' 
        ? JSON.parse(selectedProvider.importFormat) 
        : selectedProvider.importFormat)
      : null;

    if (!formatConfig?.clientMapping) {
      return fileDetails.parsedRows;
    }

    const clientMappingFields = Object.values(formatConfig.clientMapping).filter(col => col !== '') as string[];
    const seenSignatures = new Set<string>();
    const dedupedRows: any[] = [];

    (fileDetails.parsedRows as any[]).forEach(row => {
      const signature = clientMappingFields
        .map(field => row[field] || '')
        .join('|');
      
      if (!seenSignatures.has(signature)) {
        seenSignatures.add(signature);
        dedupedRows.push(row);
      }
    });

    if (dedupedRows.length < (fileDetails.parsedRows as any[]).length) {
      const removedCount = (fileDetails.parsedRows as any[]).length - dedupedRows.length;
      console.log(`Removed ${removedCount} duplicate client row(s) from file`);
    }

    return dedupedRows;
  }, [fileDetails?.parsedRows, selectedProviderId, providers]);

  React.useEffect(() => {
    if (fileDetails?.parsedRows && initializedFileRef.current !== fileDetails.parsedRows) {
      const allIds = new Set((fileDetails.parsedRows as any[]).map(row => (row as any).__rowId));
      setSelectedRowIds(allIds);
      initializedFileRef.current = fileDetails.parsedRows;
    }
  }, [fileDetails?.parsedRows]);

  React.useEffect(() => {
    if (!open) {
      fileInputRef.current = null;
      setFile(null);
      setErrors({});
      setStep(0);
      setSelectedProviderId(null);
      setSelectedRowIds(new Set());
      initializedFileRef.current = null;
      setClientDuplicates([]);
      setInvoiceDuplicates([]);
      setDuplicateCheckProgress({ currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: 0 });
      setHasProcessedStep([false, false, false, false, false, false, false]);
    } else {
      const { url, method } = buildApiRequestConfig(routes.invoiceProviders.fetch, [
        { param: 'countryId', value: companySettings?.country?.id },
      ]);
      setFetchProviders({ url, method });
    }
  }, [open, setFetchProviders, companySettings?.country?.id]);

  React.useEffect(() => {
    const { data, isLoading, error } = fetchProviders;
    if (data && !isLoading) {
      const formattedProviders = data.map((prov: any) => ({
        id: prov.id,
        name: prov.name,
        country: prov.country,
        importFormat: prov.importFormat,
      }));
      setProviders(formattedProviders);
    }
    if (error) {
      showError("Failed to fetch invoice providers");
    }
  }, [fetchProviders, showError]);

  React.useEffect(() => {
    if (!file || !selectedProviderId) return;

    const selectedProvider = providers.find(p => p.id === selectedProviderId);
    if (!selectedProvider) return;

    const readFileAsText = (f: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(f, "utf-8");
      });

    const detectSeparator = (sample: string): string => {
      const candidates = [",", "\t", ";", "|"];
      const scores = candidates.map((sep) => {
        const lines = sample.split(/\r\n|\n|\r/).slice(0, 5);
        const total = lines.reduce((acc, l) => acc + (l.split(sep).length - 1), 0);
        return { sep, total };
      });
      scores.sort((a, b) => b.total - a.total);
      return scores[0].total > 0 ? scores[0].sep : ",";
    };

    const parseDelimited = (text: string, sep: string): string[][] => {
      const rows: string[][] = [];
      let cur = "";
      let row: string[] = [];
      let inQuotes = false;

      for (let i = 0; i < text.length; i++) {
        const ch = text[i];

        if (inQuotes) {
          if (ch === '"') {
            if (text[i + 1] === '"') {
              cur += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            cur += ch;
          }
        } else {
          if (ch === '"') {
            inQuotes = true;
          } else if (ch === sep) {
            row.push(cur);
            cur = "";
          } else if (ch === '\r') {
            // ignore
          } else if (ch === '\n') {
            row.push(cur);
            rows.push(row);
            row = [];
            cur = "";
          } else {
            cur += ch;
          }
        }
      }

      if (cur !== "" || row.length > 0) {
        row.push(cur);
        rows.push(row);
      }

      return rows;
    };

    (async () => {
      try {
        const name = file.name || '';
        const ext = name.split('.').pop()?.toLowerCase();
        if (!['csv', 'tsv', 'txt'].includes(ext || '')) {
          showError('Only CSV/TSV/TXT text formats are parsed in this UI.');
          return;
        }

        const text = await readFileAsText(file);
        if (!text) {
          showError('No data found in the file.');
          return;
        }

        const sample = text.split(/\r\n|\n|\r/).slice(0, 10).join('\n');
        const sep = ext === 'tsv' ? '\t' : detectSeparator(sample);
        let rows = parseDelimited(text, sep);

        if (!rows.length) {
          showError('No rows parsed from the file.');
          return;
        }

        // Parse format configuration for the provider's import format
        const formatConfig = selectedProvider.importFormat 
          ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
          : null;
        const hasHeader = formatConfig?.header ?? true;

        // Extract headers based on format configuration
        let headers: string[];
        let dataRows: string[][];

        if (hasHeader) {
          headers = rows[0].map((cell, idx) => (cell ?? '').toString().trim() || `column_${idx}`);
          dataRows = rows.slice(1);
        } else {
          // If no header, generate column names
          const numCols = rows[0]?.length ?? 0;
          headers = Array.from({ length: numCols }, (_, idx) => `column_${idx}`);
          dataRows = rows;
        }

        let parsedRows = dataRows.map((row, idx) => {
          const obj: Record<string, string> = { __rowId: String(idx) };
          headers.forEach((header, headerIdx) => {
            obj[header] = (row[headerIdx] ?? '').trim();
          });
          return obj;
        });

        // Clear duplicate check caches when file or provider changes
        clientDuplicateCheckCacheRef.current = null;
        invoiceDuplicateCheckCacheRef.current = null;

        onFileChange({
          file,
          parsedRows: parsedRows as any,
          failedRows: [],
          result: {
            successfulRows: parsedRows.length,
            failedRows: 0,
            totalRows: rows.length,
          },
          headers: headers,
          provider: selectedProvider,
        } as FileDetails);
      } catch (err: any) {
        showError(err?.toString?.() ?? 'Failed to parse file');
      }
    })();
  }, [file, selectedProviderId, providers, onFileChange, showError]);

  const validate = (): boolean => {
    const newErrors: { file?: string } = {};
    if (!file) {
      newErrors.file = 'File is required';
    } else {
      if (file.type !== 'text/csv' && file.type !== 'text/tab-separated-values') {
        newErrors.file = 'Invalid file type. Only CSV and TSV files are allowed.';
      }
      if (file.size > 50 * 1024 * 1024) {
        newErrors.file = 'File size exceeds 50MB limit';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatFileSize = (size: number): string => {
    const kb = size / 1024;
    if (kb < 1024) return `${Math.ceil(kb)} KB`;
    return `${Math.ceil(kb / 1024)} MB`;
  };

  const checkClientDuplicatesAPI = React.useCallback(async (parsedRows: any[], companyField: string, recipientField: string, countryField: string) => {
    if (!user?.entityId || !parsedRows.length) {
      return;
    }

    try {
      // Create a hash of the parsed rows to check if it's the same file
      const dataHash = JSON.stringify(parsedRows).substring(0, 50);
      
      // Check if results are cached for this file
      if (clientDuplicateCheckCacheRef.current && clientDuplicateCheckCacheRef.current.fileDataHash === dataHash) {
        // Use cached results instead of calling API again
        setClientDuplicates(clientDuplicateCheckCacheRef.current.duplicates);
        setIsProcessing(false);
        return;
      }

      setIsProcessing(true);
      const CHUNK_SIZE = 50;
      const chunks = [];
      const allFoundDuplicates: any[] = [];

      // Create chunks from parsed rows
      for (let i = 0; i < parsedRows.length; i += CHUNK_SIZE) {
        chunks.push(parsedRows.slice(i, i + CHUNK_SIZE));
      }

      // Initialize progress
      setDuplicateCheckProgress({ currentChunk: 0, totalChunks: chunks.length, processedRows: 0, totalRows: parsedRows.length });

      // Process chunks sequentially
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        try {
          const chunk = chunks[chunkIndex];
          const { url, method } = buildApiRequestConfig(routes.client.checkDuplicate, [{ param: 'entityId', value: user.entityId }]);

          const requestData = chunk.map(item => ({
            companyName: typeof item === 'object' && item !== null ? (item as any)[companyField] : '',
            recipient: typeof item === 'object' && item !== null ? (item as any)[recipientField] : '',
            country: typeof item === 'object' && item !== null ? (item as any)[countryField] : ''
          }));

          const token = Cookies.get('token');
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          };

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await axios({
            method,
            url,
            headers,
            data: requestData
          });

          const apiDuplicates = (response.data?.duplicates || []) as any[];
          
          // Directly add duplicates from the API response using chunk-relative indices
          apiDuplicates.forEach((dup: any) => {
            const rowIndex = dup.index; // Index within the chunk (0-based)
            if (rowIndex >= 0 && rowIndex < chunk.length) {
              const row = chunk[rowIndex];
              allFoundDuplicates.push({
                ...row,
                __duplicateInfo: dup
              });
            }
          });

          // Update progress
          setDuplicateCheckProgress(prev => ({
            ...prev,
            currentChunk: chunkIndex + 1,
            processedRows: Math.min((chunkIndex + 1) * CHUNK_SIZE, parsedRows.length)
          }));

          // Small delay between chunks
          if (chunkIndex < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } catch (error) {
          console.error('Error checking duplicates in chunk:', chunkIndex, error);
        }
      }

      // Cache the results to avoid re-running API on step navigation
      clientDuplicateCheckCacheRef.current = {
        fileDataHash: dataHash,
        duplicates: allFoundDuplicates
      };

      setClientDuplicates(allFoundDuplicates);
      setIsProcessing(false);
    } catch (err: any) {
      console.error('Client duplicate check error:', err);
      setIsProcessing(false);
      showError(err?.response?.data?.message || err?.toString?.() || 'Failed to check for duplicate clients');
    }
  }, [user?.entityId, showError]);

  const checkInvoiceDuplicatesAPI = React.useCallback(async (parsedRows: any[], invoiceNumberField: string, totalField: string) => {
    try {
      // Create a hash of the parsed rows to check if it's the same file
      const dataHash = JSON.stringify(parsedRows).substring(0, 50);
      
      // Check if results are cached for this file
      if (invoiceDuplicateCheckCacheRef.current && invoiceDuplicateCheckCacheRef.current.fileDataHash === dataHash) {
        // Use cached results instead of calling API again
        setInvoiceDuplicates(invoiceDuplicateCheckCacheRef.current.duplicates);
        setIsProcessing(false);
        return;
      }

      setIsProcessing(true);

      // Group rows by invoice number to get one header row per invoice
      const invoiceGroups: Record<string, any[]> = {};
      parsedRows.forEach((row: any) => {
        const invoiceNumber = row[invoiceNumberField] || 'Unknown';
        if (!invoiceGroups[invoiceNumber]) {
          invoiceGroups[invoiceNumber] = [];
        }
        invoiceGroups[invoiceNumber].push(row);
      });

      // Get header rows (first row of each invoice group) for duplicate checking
      const headerRows = Object.values(invoiceGroups).map(group => group[0]);
      const totalInvoices = headerRows.length;

      setDuplicateCheckProgress({ currentChunk: 0, totalChunks: 0, processedRows: 0, totalRows: totalInvoices });
      
      const CHUNK_SIZE = 50;
      const chunks = [];
      for (let i = 0; i < headerRows.length; i += CHUNK_SIZE) {
        chunks.push(headerRows.slice(i, i + CHUNK_SIZE));
      }

      setDuplicateCheckProgress(prev => ({
        ...prev,
        totalChunks: chunks.length
      }));

      const allFoundDuplicates: any[] = [];

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        try {
          const { url, method } = buildApiRequestConfig(routes.invoices.checkDuplicate, [
            { param: 'entityId', value: user?.entityId || '' }
          ]);

          const requestData = chunk.map((item: any) => ({
            extInvoiceNumber: typeof item === 'object' && item !== null ? (item as any)[invoiceNumberField] : '',
            total: typeof item === 'object' && item !== null ? (item as any)[totalField] : ''
          }));

          const token = Cookies.get('token');
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          };

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await axios({
            method,
            url,
            headers,
            data: requestData
          });

          const apiDuplicates = (response.data?.duplicates || []) as any[];
          
          // For each duplicate header row found, mark ALL rows of that invoice as duplicate
          apiDuplicates.forEach((dup: any) => {
            const rowIndex = dup.index; // Index within the chunk (0-based)
            if (rowIndex >= 0 && rowIndex < chunk.length) {
              const headerRow = chunk[rowIndex];
              const invoiceNumber = headerRow[invoiceNumberField] || 'Unknown';
              const allRowsOfInvoice = invoiceGroups[invoiceNumber] || [];
              
              // Mark all rows of this invoice as duplicate
              allRowsOfInvoice.forEach(row => {
                allFoundDuplicates.push({
                  ...row,
                  __duplicateInfo: dup
                });
              });
            }
          });

          // Update progress - showing invoice count
          setDuplicateCheckProgress(prev => ({
            ...prev,
            currentChunk: chunkIndex + 1,
            processedRows: Math.min((chunkIndex + 1) * CHUNK_SIZE, totalInvoices)
          }));

          // Small delay between chunks
          if (chunkIndex < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } catch (error) {
          console.error('Error checking duplicates in chunk:', chunkIndex, error);
        }
      }

      // Cache the results to avoid re-running API on step navigation
      invoiceDuplicateCheckCacheRef.current = {
        fileDataHash: dataHash,
        duplicates: allFoundDuplicates
      };

      setInvoiceDuplicates(allFoundDuplicates);
      setIsProcessing(false);
    } catch (err: any) {
      console.error('Invoice duplicate check error:', err);
      setIsProcessing(false);
      showError(err?.response?.data?.message || err?.toString?.() || 'Failed to check for duplicate invoices');
    }
  }, [user?.entityId, showError]);

  const handleNextStep = () => {
    if (step === 0) {
      if (validate()) {
        // Create import event early
        if (onEnterEditDataStep) {
          onEnterEditDataStep();
        }

        // Run duplicate check
        const selectedRows = (dedupedClients || [])
          .filter((row: any) => selectedRowIds.has((row as any).__rowId));
        
        if (selectedRows.length > 0) {
          // Get format config
          const selectedProvider = providers.find(p => p.id === selectedProviderId);
          const formatConfig = selectedProvider?.importFormat
            ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
            : null;
          
          if (formatConfig?.clientMapping) {
            const companyField = formatConfig.clientMapping.company || '';
            const recipientField = formatConfig.clientMapping.recepient || '';
            const countryField = formatConfig.clientMapping.country || '';
            
            if (companyField && recipientField && countryField) {
              checkClientDuplicatesAPI(selectedRows, companyField, recipientField, countryField);
            }
          }
        }
        
        setHasProcessedStep((prev: boolean[]) => {
          const updated = [...prev];
          updated[1] = true;
          return updated;
        });
        setStep(1);
      }
    } else if (step === 1) {
      setHasProcessedStep((prev: boolean[]) => {
        const updated = [...prev];
        updated[2] = true;
        return updated;
      });
      setStep(2);
    } else if (step === 2) {
      // Before moving to step 3 (ClientImportProgressStep), trigger import
      const selectedRows = (dedupedClients || [])
        .filter((row: any) => selectedRowIds.has((row as any).__rowId))
        .filter((row: any) => {
          // Exclude duplicate clients
          return !clientDuplicates.some(dup => (dup as any).__rowId === (row as any).__rowId);
        });

      // If no clients to import, skip step 3 and go to step 4 (invoice duplicate check)
      if (selectedRows.length === 0) {
        // Trigger invoice duplicate check before moving to step 4
        const selectedProvider = providers.find(p => p.id === selectedProviderId);
        const formatConfig = selectedProvider?.importFormat
          ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
          : null;

        let invoiceRowsToCheck = (fileDetails?.parsedRows || [])
          .filter((row: any) => selectedRowIds.has((row as any).__rowId));
        
        // Filter by amount threshold before duplicate check
        const excludeAmountRule = formatConfig?.excludeAmountRule ?? false;
        const excludeAmountThreshold = formatConfig?.excludeAmountThreshold ?? 0;
        const amountColumn = formatConfig?.invoiceMapping?.amount;
        
        if (excludeAmountRule && amountColumn) {
          invoiceRowsToCheck = invoiceRowsToCheck.filter((row: any) => {
            const amount = parseFloat(String(row[amountColumn] || 0));
            return amount > excludeAmountThreshold;
          });
        }
        
        if (invoiceRowsToCheck.length > 0) {
          if (formatConfig?.invoiceMapping) {
            const invoiceNumberField = formatConfig.invoiceMapping.extInvoiceNumber || '';
            const totalField = formatConfig.invoiceMapping.amount || '';
            
            if (invoiceNumberField && totalField) {
              checkInvoiceDuplicatesAPI(invoiceRowsToCheck, invoiceNumberField, totalField);
            }
          }
        }

        setHasProcessedStep((prev: boolean[]) => {
          const updated = [...prev];
          updated[3] = true;
          updated[4] = true;
          return updated;
        });
        setStep(4);
        return;
      }

      const progressCallback = (state: any) => {
        setClientImportStatus(state.status || 'processing');
        setImportedClients(state.importedEntries || []);
      };

      onImport(selectedRows, selectedRowIds, progressCallback);
      
      setHasProcessedStep((prev: boolean[]) => {
        const updated = [...prev];
        updated[3] = true;
        return updated;
      });
      setStep(3);
    } else if (step === 3) {
      // ClientImportProgressStep complete, now check invoice duplicates before moving to step 4
      const selectedProvider = providers.find(p => p.id === selectedProviderId);
      const formatConfig = selectedProvider?.importFormat
        ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
        : null;

      let selectedRowsForInvoice = (fileDetails?.parsedRows || [])
        .filter((row: any) => selectedRowIds.has((row as any).__rowId));
      
      // Filter by amount threshold before duplicate check
      const excludeAmountRule = formatConfig?.excludeAmountRule ?? false;
      const excludeAmountThreshold = formatConfig?.excludeAmountThreshold ?? 0;
      const amountColumn = formatConfig?.invoiceMapping?.amount;
      
      if (excludeAmountRule && amountColumn) {
        selectedRowsForInvoice = selectedRowsForInvoice.filter((row: any) => {
          const amount = parseFloat(String(row[amountColumn] || 0));
          return amount > excludeAmountThreshold;
        });
      }
      
      if (selectedRowsForInvoice.length > 0) {
        if (formatConfig?.invoiceMapping) {
          const invoiceNumberField = formatConfig.invoiceMapping.extInvoiceNumber || '';
          const totalField = formatConfig.invoiceMapping.amount || '';
          
          if (invoiceNumberField && totalField) {
            checkInvoiceDuplicatesAPI(selectedRowsForInvoice, invoiceNumberField, totalField);
          }
        }
      }

      setHasProcessedStep((prev: boolean[]) => {
        const updated = [...prev];
        updated[4] = true;
        return updated;
      });
      setStep(4);
    } else if (step === 4) {
      // InvoiceDuplicateCheckStep complete, move to ReviewInvoiceStep
      setHasProcessedStep((prev: boolean[]) => {
        const updated = [...prev];
        updated[5] = true;
        return updated;
      });
      setStep(5);
    } else if (step === 5) {
      // Before moving to step 6 (InvoiceImportProgressStep), check if there are invoices to import
      const selectedProvider = providers.find(p => p.id === selectedProviderId);
      const formatConfig = selectedProvider?.importFormat
        ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
        : null;

      const selectedRows = (fileDetails?.parsedRows || [])
        .filter((row: any) => selectedRowIds.has((row as any).__rowId))
        .filter((row: any) => {
          // Exclude duplicate invoices
          return !invoiceDuplicates.some(dup => (dup as any).__rowId === (row as any).__rowId);
        })
        .filter((row: any) => {
          // Exclude invoices based on amount threshold if rule is enabled
          const excludeAmountRule = formatConfig?.excludeAmountRule ?? false;
          const excludeAmountThreshold = formatConfig?.excludeAmountThreshold ?? 0;
          const amountColumn = formatConfig?.invoiceMapping?.amount;
          
          if (!excludeAmountRule || !amountColumn) {
            return true; // Keep the invoice if rule is not enabled or no amount column
          }
          
          const amount = parseFloat(String(row[amountColumn] || 0));
          return amount > excludeAmountThreshold; // Keep invoices with amount > threshold
        });

      // If no invoices to import, still move to step 6 but mark it as "nothing to import"
      if (selectedRows.length === 0) {
        setHasProcessedStep((prev: boolean[]) => {
          const updated = [...prev];
          updated[6] = true;
          return updated;
        });
        setStep(6);
        return;
      }

      const progressCallback = (state: any) => {
        setInvoiceImportStatus(state.status || 'processing');
        setImportedInvoices(state.importedEntries || []);
        // Update import progress if provided
        if (state.currentChunk !== undefined) {
          setImportProgress({
            currentChunk: state.currentChunk || 0,
            totalChunks: state.totalChunks || 0,
            processedRows: state.processedRows || 0,
            totalRows: state.totalRows || 0
          });
        }
        if (state.clientsFound !== undefined) {
          setImportSummary({
            clientsFound: state.clientsFound || 0,
            clientsImported: importedClients.length,
            invoicesFound: state.invoicesFound || 0,
            invoicesImported: state.invoicesImported || 0
          });
        }
      };

      if (onImportInvoices) {
        onImportInvoices(selectedRows, selectedRowIds, progressCallback);
      }

      setHasProcessedStep((prev: boolean[]) => {
        const updated = [...prev];
        updated[6] = true;
        return updated;
      });
      setStep(6);
    } else if (step < 6) {
      setHasProcessedStep((prev: boolean[]) => {
        const updated = [...prev];
        updated[step + 1] = true;
        return updated;
      });
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const selectedProvider = providers.find(p => p.id === selectedProviderId);

  const stepRender = [
    <SelectFileStep
      file={file}
      errors={errors}
      loading={loading}
      fileInputRef={fileInputRef}
      onFileChange={setFile}
      formatFileSize={formatFileSize}
      providers={providers}
      selectedProviderId={selectedProviderId}
      onProviderChange={(id) => {
        setSelectedProviderId(id);
        onProviderChange(id);
        setFile(null);
        setErrors({});
      }}
      fileDetails={fileDetails}
    />,
    <ClientDuplicateCheckStep
      isProcessing={isProcessing}
      duplicates={clientDuplicates}
      fileDetails={fileDetails}
      duplicateCheckProgress={duplicateCheckProgress}
      selectedProvider={selectedProvider}
    />,
    <ReviewClientStep
      fileDetails={fileDetails}
      dedupedClients={dedupedClients}
      selectedRowIds={selectedRowIds}
      selectedProvider={selectedProvider}
      duplicates={clientDuplicates}
    />,
    <ClientImportProgressStep
      isProcessing={clientImportStatus === 'processing'}
      status={clientImportStatus}
      importedEntries={importedClients}
    />,
    <InvoiceDuplicateCheckStep
      isProcessing={isProcessing}
      duplicates={invoiceDuplicates}
      fileDetails={fileDetails}
      duplicateCheckProgress={duplicateCheckProgress}
      selectedProvider={selectedProvider}
    />,
    <ReviewInvoiceStep
      fileDetails={fileDetails}
      selectedRowIds={selectedRowIds}
      selectedProvider={selectedProvider}
      duplicates={invoiceDuplicates}
    />,
    // Step 6: Check if there are invoices to import
    (() => {
      const selectedProvider = providers.find(p => p.id === selectedProviderId);
      const formatConfig = selectedProvider?.importFormat
        ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
        : null;

      const selectedRows = (fileDetails?.parsedRows || [])
        .filter((row: any) => selectedRowIds.has((row as any).__rowId))
        .filter((row: any) => {
          return !invoiceDuplicates.some(dup => (dup as any).__rowId === (row as any).__rowId);
        })
        .filter((row: any) => {
          // Exclude invoices based on amount threshold if rule is enabled
          const excludeAmountRule = formatConfig?.excludeAmountRule ?? false;
          const excludeAmountThreshold = formatConfig?.excludeAmountThreshold ?? 0;
          const amountColumn = formatConfig?.invoiceMapping?.amount;
          
          if (!excludeAmountRule || !amountColumn) {
            return true; // Keep the invoice if rule is not enabled or no amount column
          }
          
          const amount = parseFloat(String(row[amountColumn] || 0));
          return amount > excludeAmountThreshold; // Keep invoices with amount > threshold
        });

      // If no invoices to import, always show nothing to import message
      if (selectedRows.length === 0) {
        return <NothingToImportStep clientsImported={importedClients.length} />;
      }

      // Otherwise show import progress
      return (
        <InvoiceImportProgressStep
          isProcessing={invoiceImportStatus === 'processing'}
          status={invoiceImportStatus}
          importedEntries={importedInvoices}
          clientsFound={importSummary.clientsFound}
          clientsImported={importSummary.clientsImported}
          invoicesFound={importSummary.invoicesFound}
          invoicesImported={importSummary.invoicesImported}
          importProgress={importProgress}
        />
      );
    })(),
  ];

  // Check if there are actually invoices to import at step 6
  const formatConfig = selectedProvider?.importFormat
    ? (typeof selectedProvider.importFormat === 'string' ? JSON.parse(selectedProvider.importFormat) : selectedProvider.importFormat)
    : null;

  const hasInvoicesToImport = (fileDetails?.parsedRows || [])
    .filter((row: any) => selectedRowIds.has((row as any).__rowId))
    .filter((row: any) => !invoiceDuplicates.some(dup => (dup as any).__rowId === (row as any).__rowId))
    .filter((row: any) => {
      // Exclude invoices based on amount threshold if rule is enabled
      const excludeAmountRule = formatConfig?.excludeAmountRule ?? false;
      const excludeAmountThreshold = formatConfig?.excludeAmountThreshold ?? 0;
      const amountColumn = formatConfig?.invoiceMapping?.amount;
      
      if (!excludeAmountRule || !amountColumn) {
        return true; // Keep the invoice if rule is not enabled or no amount column
      }
      
      const amount = parseFloat(String(row[amountColumn] || 0));
      return amount > excludeAmountThreshold; // Keep invoices with amount > threshold
    })
    .length > 0;

  return (
    <>
      <Dialog
        open={open}
        maxWidth={isMobile ? false : "md"}
        fullWidth={isMobile}
        {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
        sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
        PaperProps={{
          sx: isMobile
            ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, p: 0, display: 'flex', flexDirection: 'column' }
            : { borderRadius: 4, p: 0, height: 550, display: 'flex', flexDirection: 'column' }
        }}
      >
      <DialogContent sx={{ p: 0, pt: 3, pb: 0, px: isMobile ? 2 : 4, width: isMobile ? '100%' : 800, overflowX: 'hidden', overflowY: 'auto', position: 'relative', flex: 1 }}>
        <IconButton
          onClick={onCancel}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            '&:hover': {
              backgroundColor: '#e0e0e0',
            },
            width: 32,
            height: 32,
          }}
        >
          <X size={18} />
        </IconButton>
        <>
          {fileDetails?.result?.totalRows ? (
            <Typography textAlign="center" fontSize={14} fontWeight={500} mb={2}>
              There are <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fileDetails?.result?.totalRows}</span> lines in the uploaded file, with <span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fileDetails?.result?.successfulRows}</span> entries identified.
            </Typography>
          ):
          (
            <Typography textAlign="center" fontSize={14} fontWeight={500} mb={2}>Select a file to start the import process.</Typography>
          )
          }
          <Box display="flex" justifyContent="center" mb={5}>
            <StepLineIndicator
              steps={7}
              activeStep={step}
              width={isMobile ? '100%' : 800}
              stepNames={['Select File', 'Check Clients', 'Review Clients', 'Import Clients', 'Check Invoices', 'Review Invoices', 'Import Invoices']}
              forceCompletedSteps={(() => {
                const completed = [];
                if (clientImportStatus === 'success' || clientImportStatus === 'error') {
                  completed.push(3);
                }
                if (invoiceImportStatus === 'success' || invoiceImportStatus === 'error') {
                  completed.push(6);
                }
                return completed;
              })()}
            />
          </Box>
        </>

        {stepRender[step]}
      </DialogContent>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', justifyContent: "space-between", gap: 2, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
        {step > 0 && step !== 3 && step !== 6 && (
          <Button
            variant="outlined"
            color="primary"
            sx={{ 
              borderRadius: 2, 
              minWidth: 120, 
              fontSize: 14, 
              py: 1.2, 
              textTransform: 'none', 
              color: 'var(--color-primary)', 
              height: 35, 
              background: 'var(--color-page-background)', 
              border: '1px solid #00b59e' 
            }}
            onClick={handlePrevStep}
          >
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {step < 6 && (
          <Button
            variant="contained"
            color="primary"
            disabled={loading || (step === 0 && !file) || (step === 1 && isProcessing) || (step === 4 && isProcessing)}
            sx={{ 
              borderRadius: 2, 
              minWidth: 120, 
              fontSize: 14, 
              py: 1.2, 
              textTransform: 'none', 
              color: '#fff', 
              height: 35,
              background: loading || (step === 0 && !file) || (step === 1 && isProcessing) || (step === 4 && isProcessing) ? '#ccc' : 'var(--color-primary)',
            }}
            onClick={handleNextStep}
          >
            {loading || (step === 1 && isProcessing) || (step === 4 && isProcessing) ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} sx={{ color: '#fff' }} />
                <span>{(step === 1 || step === 4) ? 'Checking...' : 'Processing...'}</span>
              </Box>
            ) : (
              'Next'
            )}
          </Button>
        )}
        {step === 6 && (
          <Button
            variant="contained"
            color="primary"
            disabled={invoiceImportStatus === 'processing' && hasInvoicesToImport}
            sx={{ 
              borderRadius: 2, 
              minWidth: 120, 
              fontSize: 14, 
              py: 1.2, 
              textTransform: 'none', 
              color: '#fff', 
              height: 35,
              background: invoiceImportStatus === 'processing' && hasInvoicesToImport ? '#ccc' : 'var(--color-primary)',
            }}
            onClick={onCancel}
          >
            {invoiceImportStatus === 'processing' && hasInvoicesToImport ? 'Importing...' : 'Close'}
          </Button>
        )}
      </Box>
      </Dialog>
    
    </>
  );
};

export default ImportInvoicePopup;
